import React,{ useState, useEffect, useRef } from 'react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { 
  Box, 
  Avatar, 
  Typography, 
  IconButton, 
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { modernTableStyle, modernTableOptions } from '../common/tableTheme';
import UserCreateDialog from '../components/UserCreateDialog';

export default function Users() {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const {
    addUser,
    editUser, 
    deleteUser,
    checkUserExists,
    fetchUsersOnce
  } = api;
  const [data, setData] = useState([]);
  const staticusers = useSelector(state => state.usersdata.staticusers || state.usersdata.users);
  const dispatch = useDispatch();
  const loaded = useRef(false);

  // Estados para modal de edição e exclusão
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);

  useEffect(()=>{
    dispatch(fetchUsersOnce());
  },[dispatch,fetchUsersOnce]);

  useEffect(()=>{
    if(staticusers && Array.isArray(staticusers)){
      setData(staticusers.filter(user => 
        user.usertype === 'admin' || 
        user.usertype === 'superadmin' || 
        user.role === 'admin' || 
        user.role === 'superadmin' ||
        Boolean(user.is_superadmin)
      ));
    }else{
      setData([]);
    }
    loaded.current = true;
  },[staticusers]);

  const columns = [
    { 
      title: 'Administrador', 
      field: 'firstName', 
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar 
            src={rowData.profile_image} 
            sx={{ width: 40, height: 40, bgcolor: '#0f172a', fontSize: '0.875rem', fontWeight: 700 }}
          >
            {rowData.firstName?.[0] || 'A'}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.875rem', lineHeight: 1.2 }}>
              {rowData.firstName} {rowData.lastName}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
              {rowData.email || 'Sem e-mail'}
            </Typography>
          </Box>
        </Box>
      )
    },
    { 
      title: t('mobile') || 'Telefone', 
      field: 'mobile', 
      render: rowData => rowData.mobile || '—'
    },
    { 
      title: t('email') || 'E-mail', 
      field: 'email', 
      render: rowData => rowData.email || '—' 
    },
    {
      title: 'Ações',
      field: 'actions',
      cellStyle: { textAlign: 'center', width: 110 },
      headerStyle: { textAlign: 'center', width: 110 },
      render: rowData => (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Tooltip title="Editar Administrador">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditingUser(rowData);
              }}
              sx={{
                color: '#0284c7',
                bgcolor: '#f0f9ff',
                border: '1px solid #bae6fd',
                '&:hover': { bgcolor: '#e0f2fe' }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir Administrador">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteConfirmUser(rowData);
              }}
              sx={{
                color: '#ef4444',
                bgcolor: '#fef2f2',
                border: '1px solid #fecaca',
                '&:hover': { bgcolor: '#fee2e2' }
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const [selectedRow, setSelectedRow] = useState(null);

  return (
    !loaded.current? <CircularLoading/>:
    <div>
      <MaterialTable
        title=""
        columns={columns}
        style={modernTableStyle}
        data={data}
        onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
        options={modernTableOptions(selectedRow, {
          searchPlaceholder: "Buscar administrador...",
          maxBodyHeight: "calc(100vh - 280px)"
        })}
        localization={{
          body:{
            addTooltip: (t('add')),
            deleteTooltip: (t('delete')),
            editTooltip: (t('edit')),
            emptyDataSourceMessage: (t('blank_message')),
          },
          toolbar: {
            searchPlaceholder: (t('search')),
            exportTitle: (t('export')),
          },
          header: {
            actions: (t('actions')) 
          },
          pagination: {
            labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
            firstTooltip: (t('first_page_tooltip')),
            previousTooltip: (t('previous_page_tooltip')),
            nextTooltip: (t('next_page_tooltip')),
            lastTooltip: (t('last_page_tooltip'))
          },
        }}
      />

      {/* Modal Unificado de Edição */}
      <UserCreateDialog
        open={Boolean(editingUser)}
        userToEdit={editingUser}
        onClose={() => setEditingUser(null)}
        onSuccess={() => {
          setEditingUser(null);
          dispatch(fetchUsersOnce());
        }}
        defaultRole="admin"
      />

      {/* Diálogo de Confirmação de Exclusão */}
      <Dialog
        open={Boolean(deleteConfirmUser)}
        onClose={() => setDeleteConfirmUser(null)}
        PaperProps={{ sx: { borderRadius: '16px', p: 1, maxWidth: 440 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
          Excluir Administrador?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#475569' }}>
            Deseja realmente remover o administrador <strong>{deleteConfirmUser?.firstName} {deleteConfirmUser?.lastName}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDeleteConfirmUser(null)} sx={{ color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (deleteConfirmUser?.id) {
                if (deleteConfirmUser.id === 'admin0001') {
                  alert(t('first_admin_deleted'));
                } else {
                  dispatch(deleteUser(deleteConfirmUser.id));
                }
                setDeleteConfirmUser(null);
                setTimeout(() => dispatch(fetchUsersOnce()), 600);
              }
            }}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
